<template>
  <e-sidebar
    id="sidebar-form-event-product"
    :title="isEdit ? $t('Editar produto') : $t('Adicionar produto')"
    :show="showSidebar"
    :fetching="fetching"
    :saving="saving"
    width="600px"
    @save="onSaveItem"
    @hidden="hide"
  >
    <template #content>
      <FormulateForm
        ref="itemSidebarForm"
        name="itemSidebarForm"
      >
        <b-row>
          <b-col>
            <FormulateInput
              v-if="isEdit"
              id="event_product_sidebar-sku_id"
              v-model="itemForm.sku.name"
              type="label"
              :label="$t('Produto')"
            />
            <e-search-sku
              v-else
              id="event_product_sidebar-sku_id"
              v-model="itemForm.sku"
              :label="$t('Produto')"
              input-id="event_product_sidebar-sku_product"
              value-is-object
              :store-id="storeId"
              :price-table-id="priceTableId"
              :default-options="defaultSkuOptions"
              autofocus
              @input="onSelectProduct"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col md="5">
            <FormulateInput
              v-if="isEdit"
              id="event_product_sidebar-quantity"
              v-model="itemForm.quantity"
              type="label"
              :label="$t('Quantidade reservada')"
              :instruction="$t('Quantidade a ser reservada para este produto no evento')"
            />
            <FormulateInput
              v-else
              id="event_product_sidebar-quantity"
              v-model.number="itemForm.quantity"
              type="text-number"
              class="required"
              :disabled="isEdit"
              :label="$t('Quantidade reservada')"
              :instruction="$t('Quantidade a ser reservada para este produto no evento')"
              validation="required|min:1"
            />
          </b-col>
        </b-row>

        <b-row v-if="itemForm.id">
          <b-col md="12">
            <b-button
              variant="primary"
              :disabled="maxQuantityToAdd === 0"
              @click="addSkuStock"
            >{{
              $t('Adicionar reserva de estoque')
            }}</b-button>
            <b-button
              class="ml-1"
              variant="outline-primary"
              :disabled="!itemForm.quantity"
              @click="removeSkuStock"
            >{{
              $t('Remover reserva de estoque')
            }}</b-button>
          </b-col>
          <b-col
            v-if="itemForm.quantityAdded != null"
            md="5"
          >
            <FormulateInput
              id="event_product_sidebar-quantityAdded"
              v-model.number="itemForm.quantityAdded"
              type="number"
              class="required mt-1"
              :label="$t('Quantidade a adicionar')"
              :instruction="quantityAddedInstruction"
              :validation="quantityAddedValidation"
            />
          </b-col>
          <b-col
            v-if="itemForm.quantityRemoved != null"
            md="5"
          >
            <FormulateInput
              id="event_product_sidebar-quantityRemoved"
              v-model.number="itemForm.quantityRemoved"
              type="number"
              class="required mt-1"
              :label="$t('Quantidade a devolver ao estoque da loja')"
              :instruction="
                $t('Devolve parte da reserva do evento para o estoque disponível da loja')
              "
              :validation="`required|min:1|max:${itemForm.quantity}`"
            />
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col>
            <div>
              <p class="h6 mb-0">
                {{ $t('Quant. em estoque') }}
              </p>
              <p
                class="h4 text-bold"
                :class="{ 'text-danger': infoValues.stock < 0 }"
              >
                {{ infoValues.stock === 0 ? 0 : infoValues.stock || '-' }}
              </p>
            </div>
          </b-col>
        </b-row>
      </FormulateForm>
    </template>
  </e-sidebar>
</template>

<script>
import { mapState } from 'vuex'
import _ from 'lodash'
import { BRow, BCol, BButton } from 'bootstrap-vue'
import ESidebar from '@/views/components/ESidebar.vue'
import { authorizationRules, discountTypes, formulateHelper } from '@/mixins'
import { getInitialItemForm } from '@/store/pages/sale/event/event-maintain'
import ESearchSku from '@/views/components/inputs/ESearchSku.vue'

const getInitialInfoValues = () => ({
  productPrice: 0,
  subtotal: 0,
  total: 0,
  totalDiscount: 0,
  priceTableDiscountTotal: 0,
  stock: null,
  promotionId: null,
  promotionQuantityAvailable: 0,
  promotionQuantityUnlimited: false,
})

export default {
  components: { BRow, BCol, BButton, ESidebar, ESearchSku },

  mixins: [discountTypes, formulateHelper, authorizationRules],

  props: {
    storeId: {
      type: [String, Number],
      required: true,
    },
    priceTableId: {
      type: [String, Number],
      default: '',
    },
  },

  data() {
    return {
      showSidebar: false,
      fetching: false,
      saving: false,
      itemForm: getInitialItemForm(),
      infoValues: getInitialInfoValues(),
      productEnriched: getInitialItemForm(),
      delegateUserData: null,
    }
  },

  computed: {
    ...mapState('pages/sale/event/eventMaintain', ['event']),
    isEdit() {
      return !!this.itemForm.id || !!this.itemForm.localId
    },
    /**
     * Saldo disponível do SKU na loja hoje. Pode ser negativo em produto que já tinha reserva
     * maior que o estoque, e é esse valor bruto que diz quanto uma devolução realmente recompõe.
     * `null` quando o saldo é desconhecido (usuário sem permissão de ver estoque) — aí o limite
     * fica só no back, que também valida.
     */
    currentAvailableStock() {
      const stock = this.productEnriched?.sku?.stock
      return _.isNil(stock) ? null : Number(stock)
    },

    /** Teto da reserva que pode ser adicionada: saldo negativo não libera nada. */
    maxQuantityToAdd() {
      if (this.currentAvailableStock == null) return null
      return Math.max(this.currentAvailableStock, 0)
    },

    quantityAddedValidation() {
      const rules = ['required', 'min:1']
      if (this.maxQuantityToAdd != null) rules.push(`max:${this.maxQuantityToAdd}`)
      return rules.join('|')
    },

    quantityAddedInstruction() {
      if (this.maxQuantityToAdd == null) {
        return this.$t('Quantidade a reservar do estoque disponível da loja')
      }
      return `${this.$t('Disponível no estoque da loja')}: ${this.maxQuantityToAdd}`
    },

    defaultSkuOptions() {
      if (this.itemForm.sku) {
        const { id, name } = this.itemForm.sku
        return [
          {
            id, // : String(id),
            value: id, // String(id),
            name,
          },
        ]
      }
      return []
    },
  },

  methods: {
    async show(item) {
      this.cleanSidebar()
      this.showSidebar = true

      if (item) {
        try {
          this.fetching = true
          await new Promise(resolve => setTimeout(() => resolve(), 200))
          // clona o sku: `fetchSkuStockInfo` grava o estoque em `productEnriched.sku` e,
          // sem a cópia, escreveria direto no item da lista do evento (state do vuex).
          this.itemForm = { ...getInitialItemForm(), ...item, sku: { ...item.sku } }
          this.productEnriched = { ...getInitialItemForm(), ...item, sku: { ...item.sku } }
          await this.fetchSkuStockInfo()
          this.updateInfos()
        } finally {
          this.fetching = false
        }
      }
    },

    hide() {
      this.cleanSidebar()
      this.showSidebar = false
    },

    cleanSidebar() {
      this.itemForm = getInitialItemForm()
      this.productEnriched = getInitialItemForm()
      this.infoValues = getInitialInfoValues()
    },

    async onSaveItem() {
      try {
        this.saving = true
        this.$refs.itemSidebarForm.showErrors()
        if (this.$refs.itemSidebarForm.hasErrors) {
          this.showInvalidDataMessage()
          return
        }

        let eventSkuId = this.itemForm.id

        const { quantityAdded, quantityRemoved } = this.itemForm
        // Num produto já gravado o save só serve para aplicar um ajuste de reserva. Sem ajuste
        // informado não há nada a enviar: antes caía no `?? itemForm.quantity` e reservava de
        // novo toda a quantidade do produto só por abrir e salvar.
        const isAdjustment = quantityAdded != null || quantityRemoved != null

        if (this.event.id && (isAdjustment || !this.isEdit)) {
          const isRemoval = quantityRemoved != null

          const { data } = await this.$http.post(
            `/api/sales/events/${this.event.id}/${isRemoval ? 'remove-sku-stock' : 'add-sku-stock'}`,
            {
              eventId: this.event.id,
              skuId: this.itemForm.sku?.id ?? this.itemForm.skuId,
              quantity: isRemoval ? quantityRemoved : quantityAdded ?? this.itemForm.quantity,
            }
          )

          eventSkuId = data
          this.showSuccess({
            message: isRemoval
              ? this.$t('Reserva de estoque devolvida ao estoque da loja')
              : this.$t('Reserva de estoque adicionada com sucesso'),
          })
        }

        if (this.isEdit) {
          this.$emit('update', { ...this.itemForm, availableStock: this.nextAvailableStock() })
        } else {
          this.$emit('add', { ...this.itemForm, ...this.productEnriched, id: eventSkuId })
        }

        this.hide()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    async onSelectProduct(productSearched) {
      if (!productSearched) return
      try {
        this.saving = true
        if (!productSearched) return
        const { ean } = productSearched

        const { data } = await this.$http.get(
          `/api/sales/pay-box-sku/store/${this.storeId}/ean/${ean}`,
          { params: { priceTableId: this.priceTableId } }
        )

        if (data?.stock <= 0 && data?.hasStock === false) {
          await this.confirm({
            title: this.$t('Produto sem estoque'),
            text: this.$t(`Este produto não pode ser adicionado pois está sem estoque.`),
            cancelButtonText: this.$t('Continuar'),
            showCancelButton: true,
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: true,
            focusCancel: true,
          })
          this.cleanSidebar()
          this.focusInput('#event_product_sidebar-sku_product')
          return
        }

        this.focusInput('#event_product_sidebar-quantity')

        const product = {
          skuId: data.skuId,
          sku: { ...data, label: data.name },
          price: data.price,
          promotionId: data.promotionId,
        }
        this.productEnriched = product
        this.updateInfos()
      } catch (error) {
        this.itemForm.sku = null
        this.productEnriched = getInitialItemForm()
        this.infoValues = getInitialInfoValues()
        this.showError({ error })
        this.focusInput('#event_product_sidebar-sku_product', false)
      } finally {
        this.saving = false
      }
    },

    // eslint-disable-next-line func-names
    updateInfos: _.debounce(function () {
      const { stock } = this.productEnriched?.sku || {}

      this.infoValues = {
        stock: stock ?? null,
      }
    }, 400),

    async fetchSkuStockInfo() {
      // Produto já gravado no evento: o GET do evento devolve `availableStock`, o saldo do SKU
      // na loja. Sem isso não havia como buscar o estoque aqui — o payload do evento não traz o
      // EAN do SKU, a consulta por EAN era pulada e "Quant. em estoque" ficava em branco,
      // liberando reserva de qualquer quantidade.
      if (!_.isNil(this.itemForm.availableStock)) {
        this.productEnriched.sku = {
          ...this.productEnriched.sku,
          stock: this.itemForm.availableStock,
        }
        return
      }

      const ean = this.productEnriched?.sku?.ean
      if (!_.isNil(this.productEnriched?.sku?.stock) || !ean) {
        return
      }
      const { data } = await this.$http.get(
        `/api/sales/pay-box-sku/store/${this.storeId}/ean/${ean}`,
        { params: { priceTableId: this.priceTableId } }
      )

      this.productEnriched.sku.stock = data.stock

      if (this.itemForm.id) {
        const isToRefreshPromotionData =
          this.itemForm.promotionId &&
          data.promotionId &&
          this.itemForm.promotionId === data.promotionId
        if (isToRefreshPromotionData) {
          const promotionData = {
            priceFrom: data.priceFrom,
            promotionQuantityAvailable: data.promotionQuantityAvailable,
            promotionQuantityUnlimited: data.promotionQuantityUnlimited,
          }

          this.itemForm.sku = {
            ...this.itemForm.sku,
            ...promotionData,
          }

          this.productEnriched.sku = {
            ...this.productEnriched.sku,
            ...promotionData,
          }

          this.updateInfos()
        }
      }
    },

    /**
     * Saldo da loja depois do ajuste: adicionar reserva consome o disponível, devolver repõe.
     * A tela não recarrega o evento após salvar, então sem isso o sidebar reabriria com o
     * saldo anterior. Parte do saldo bruto, não do teto de reserva: com disponível -530,
     * devolver 530 deixa 0, e não 530.
     */
    nextAvailableStock() {
      if (this.currentAvailableStock == null) return null
      const added = this.itemForm.quantityAdded || 0
      const removed = this.itemForm.quantityRemoved || 0
      return this.currentAvailableStock - added + removed
    },

    addSkuStock() {
      // Adicionar e devolver são exclusivos: o save envia um único ajuste por vez.
      this.itemForm.quantityRemoved = null
      this.itemForm.quantityAdded = 1
    },

    removeSkuStock() {
      this.itemForm.quantityAdded = null
      this.itemForm.quantityRemoved = 1
    },
  },
}
</script>

<style lang="scss" scoped>
.text-bold {
  font-weight: 800;
}
</style>
